import { Button, Flex, Grid } from '@radix-ui/themes';
import { createFileRoute, Link, Outlet, useLoaderData, useLocation, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { TbAlertTriangle, TbHeadset, TbHome, TbLayoutGridAdd, TbSettingsAutomation } from 'react-icons/tb';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';
import { AccountDropdown } from '~/components/AccountDropdown/AccountDropdown';
import { Header } from '~/components/Header/Header';
import { TrialCountdownButton } from '~/components/Header/TrialCountdownButton';
import { Loading } from '~/components/Loading';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';
import { SlidingTooltip } from '~/features/home/components/SlidingTooltip';
import { useFeature } from '~/hooks/useFeature';
import { versoriApiFetchClient } from '~/services/versori-api/versoriApi';
import Styles from '~/styles/routes.module.scss';
import { useBreakpoint } from '~/utils/breakpoints';
import { generateRandomProjectName } from '~/utils/generateRandomName';

const searchSchema = z.object({
  onboarding: z.boolean().optional(),
  org: z.string().optional(),
  a: z.boolean().optional(),
});

export const Route = createFileRoute('/_auth/_appLayout')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps }) => {
    // Check if onboarding query param exists and create project
    if (deps.search?.onboarding && context.organisation) {
      try {
        // Create a new project using the fetch client
        const response = await versoriApiFetchClient.POST('/o/{organisation_id}/projects', {
          params: {
            path: {
              organisation_id: context.organisation.id,
            },
          },
          body: {
            name: generateRandomProjectName(),
          },
        });

        if (response.data) {
          // Return the project ID for navigation in component
          return { onboardingProjectId: response.data.id, avalaraUser: deps.search?.a };
        }
      } catch (error) {
        console.error('Failed to create project during onboarding:', error);
        // If project creation fails, continue to normal flow
      }
    }

    return { onboardingProjectId: null };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const breakpoint = useBreakpoint();
  const [enableAutomations] = useLocalStorage(
    'enableAutomations',
    localStorage.getItem('enableAutomations') === 'true' || false
  );
  const { onboardingProjectId, avalaraUser } = useLoaderData({ from: '/_auth/_appLayout' });

  // Handle onboarding navigation
  useEffect(() => {
    if (onboardingProjectId) {
      navigate({ to: '/integrations/$projectId', params: { projectId: onboardingProjectId }, ...(avalaraUser ? { search: { a: avalaraUser } } : {}) });
      return;
    }
  }, [onboardingProjectId, navigate]);

  if (onboardingProjectId) {
    return (
      <Flex justify="center" align="center" height="100vh" width="100%">
        <LoadingSpinner width="6rem" height="6rem" />
      </Flex>
    );
  }

  return (
    <Grid
      height="100vh"
      width="100%"
      style={{
        display: 'grid',
        gridTemplateAreas: `
        "header"
        "interface"
      `,
        gridTemplateRows: '5.5rem 1fr',
      }}
    >
      <Header disableLink className={Styles.LayoutHeader}>
        <Flex gap="2">
          <TrialCountdownButton />
          <Button
            variant="outline"
            color="gray"
            size="2"
            onClick={() => window.open('https://www.versori.com/demo', '_blank')}
          >
            <TbHeadset />
            {breakpoint !== 'initial' && breakpoint !== 'xs' ? 'Book a Demo' : 'Demo'}
          </Button>
          <AccountDropdown isPopoverOpen={isPopoverOpen} setIsPopoverOpen={setIsPopoverOpen} />
        </Flex>
      </Header>
      <Grid
        className={Styles.HomeLayout}
        areas={`
          "navbar content"
        `}
        columns="6.5rem 1fr"
        rows="1fr"
        minHeight="0"
        flexGrow="1"
      >
        <nav className={Styles.HomeLayoutNavBar}>
          <SlidingTooltip content="Home">
            <Link to="/" className={Styles.HomeLayoutNavBarItem} data-active={location.pathname === '/'}>
              <TbHome fontSize="2rem" color={location.pathname === '/' ? 'var(--cyan-12)' : 'var(--gray-11)'} />
            </Link>
          </SlidingTooltip>
          {enableAutomations && (
            <SlidingTooltip content="Automations">
              <Link
                to="/automations"
                className={Styles.HomeLayoutNavBarItem}
                data-active={location.pathname === '/automations'}
              >
                <TbSettingsAutomation
                  fontSize="2rem"
                  color={location.pathname === '/automations' ? 'var(--cyan-12)' : 'var(--gray-11)'}
                />
              </Link>
            </SlidingTooltip>
          )}
          <SlidingTooltip content="Issues">
            <Link to="/issues" className={Styles.HomeLayoutNavBarItem} data-active={location.pathname === '/issues'}>
              <TbAlertTriangle
                fontSize="2rem"
                color={location.pathname === '/issues' ? 'var(--cyan-12)' : 'var(--gray-11)'}
              />
            </Link>
          </SlidingTooltip>
          <SlidingTooltip content="Skills">
            <Link
              to="/resources"
              className={Styles.HomeLayoutNavBarItem}
              data-active={location.pathname === '/resources'}
            >
              <TbLayoutGridAdd
                fontSize="2rem"
                color={location.pathname === '/resources' ? 'var(--cyan-12)' : 'var(--gray-11)'}
              />
            </Link>
          </SlidingTooltip>
        </nav>
        <Flex className={Styles.HomeLayoutContent}>
          <div className={Styles.HomeLayoutContentItem}>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </div>
        </Flex>
      </Grid>
    </Grid>
  );
}
